/**
 * How a CV photo is fitted into its box on the page.
 *
 * The box is a fixed rectangle in a printed layout, so a picture that is not
 * that shape has to lose something. This module holds the one decision about
 * WHAT it loses — a pan, a zoom, a quarter turn and the shape of the box — and
 * the one function that applies it. The crop dialog draws through it, and the
 * finished picture is rendered through it, so the frame the user sets is the
 * rectangle that prints.
 */

/** Box shapes offered, as width ÷ height. */
export const ATS_PHOTO_ASPECTS = [
    { id: 'square', value: 1 },
    // 3:4 is the passport / government-form shape most Bangladeshi applicants
    // already have a photo in, and the one this tool's users arrive with.
    { id: 'portrait', value: 3 / 4 },
    { id: 'tall', value: 4 / 5 },
];

export const DEFAULT_ATS_PHOTO_CROP = {
    aspect: 1,
    // 1 means "just covers the box"; the picture is never smaller than the box
    // by default, because a CV photo with white margins inside it reads as a
    // mistake rather than a choice.
    zoom: 1,
    // Fractions of the frame, so the same numbers hold at any preview size.
    offsetX: 0,
    offsetY: 0,
    rotation: 0,
};

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 4;

/** Longest edge of the stored picture — a 24mm box needs nothing more. */
export const ATS_PHOTO_OUTPUT_EDGE = 900;
/** Longest edge kept of the ORIGINAL, so the crop can be reopened and redone. */
export const ATS_PHOTO_SOURCE_EDGE = 1400;

const sizeOf = (image) => ({
    width: image?.naturalWidth || image?.width || 0,
    height: image?.naturalHeight || image?.height || 0,
});

/**
 * The picture's drawn size inside a frame, at this crop.
 *
 * A quarter turn swaps which of the picture's edges faces which of the frame's,
 * so the cover scale has to be worked out on the TURNED size — otherwise a
 * rotated portrait leaves the frame half empty.
 */
export function atsPhotoLayout(image, frameW, frameH, crop) {
    const { width, height } = sizeOf(image);
    if (!width || !height || !frameW || !frameH) return null;

    const turned = crop.rotation === 90 || crop.rotation === 270;
    const spanW = turned ? height : width;
    const spanH = turned ? width : height;

    const cover = Math.max(frameW / spanW, frameH / spanH);
    const scale = cover * (crop.zoom || 1);
    return {
        // Size on the frame, after the turn.
        drawnW: spanW * scale,
        drawnH: spanH * scale,
        // Size in the picture's own orientation, which is what `drawImage` takes.
        naturalW: width * scale,
        naturalH: height * scale,
    };
}

/**
 * Keep the crop legal: zoom inside its range, and the picture never dragged so
 * far that the frame shows empty paper where the picture could have been.
 */
export function clampAtsPhotoCrop(crop, image, frameW, frameH) {
    const next = {
        ...DEFAULT_ATS_PHOTO_CROP,
        ...crop,
        zoom: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, crop.zoom || 1)),
    };
    const layout = atsPhotoLayout(image, frameW, frameH, next);
    if (!layout) return next;

    // Slack is what is left once the frame is covered; below zoom 1 there is
    // none, and the picture stays centred rather than sliding off its own box.
    const slackX = Math.max(0, (layout.drawnW - frameW) / 2) / frameW;
    const slackY = Math.max(0, (layout.drawnH - frameH) / 2) / frameH;
    next.offsetX = Math.min(slackX, Math.max(-slackX, next.offsetX || 0));
    next.offsetY = Math.min(slackY, Math.max(-slackY, next.offsetY || 0));
    return next;
}

/**
 * Paint the crop into a 2D context of exactly `frameW × frameH`.
 *
 * White behind, always: the picture may not cover the box at low zoom, and a
 * transparent gap would show whatever the page has underneath — which on the
 * CV is text.
 */
export function drawAtsPhotoCrop(ctx, image, frameW, frameH, crop) {
    ctx.save();
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, frameW, frameH);

    const layout = atsPhotoLayout(image, frameW, frameH, crop);
    if (!layout) { ctx.restore(); return; }

    ctx.beginPath();
    ctx.rect(0, 0, frameW, frameH);
    ctx.clip();

    ctx.translate(
        frameW / 2 + (crop.offsetX || 0) * frameW,
        frameH / 2 + (crop.offsetY || 0) * frameH,
    );
    ctx.rotate(((crop.rotation || 0) * Math.PI) / 180);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, -layout.naturalW / 2, -layout.naturalH / 2, layout.naturalW, layout.naturalH);
    ctx.restore();
}

/** The finished picture, at the shape and framing the crop describes. */
export function renderAtsPhotoCrop(image, crop, edge = ATS_PHOTO_OUTPUT_EDGE) {
    const aspect = crop.aspect || 1;
    const width = aspect >= 1 ? edge : Math.round(edge * aspect);
    const height = aspect >= 1 ? Math.round(edge / aspect) : edge;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, width);
    canvas.height = Math.max(1, height);
    drawAtsPhotoCrop(canvas.getContext('2d'), image, canvas.width, canvas.height, crop);
    return canvas.toDataURL('image/jpeg', 0.9);
}

/**
 * Where to start a picture nobody has framed yet.
 *
 * Centred horizontally, but pulled to the TOP of a picture that is taller than
 * the box. A CV photo is a portrait, a portrait has the face in its upper half,
 * and taking a square out of the middle of one is precisely how the old field
 * cut the top of people's heads off. The user can still drag it anywhere.
 */
export function defaultAtsPhotoCrop(image, frameW, frameH, aspect) {
    const base = { ...DEFAULT_ATS_PHOTO_CROP, aspect: aspect || DEFAULT_ATS_PHOTO_CROP.aspect };
    const layout = atsPhotoLayout(image, frameW, frameH, base);
    if (!layout) return base;
    const slackY = Math.max(0, (layout.drawnH - frameH) / 2) / frameH;
    return { ...base, offsetY: slackY };
}

/** Read a file into an image, downscaled so a draft does not carry a phone photo. */
export async function loadAtsPhotoSource(file, maxEdge = ATS_PHOTO_SOURCE_EDGE) {
    const src = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const image = await loadAtsPhotoImage(src);
    const { width, height } = sizeOf(image);
    const longest = Math.max(width, height);
    if (longest <= maxEdge) return src;

    const scale = maxEdge / longest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.92);
}

export function loadAtsPhotoImage(src) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
    });
}
